import type { ApiMessage } from '@interfaces/message';
import type { TelegramUser } from '@interfaces/telegram-user';

import type { ProfileTabType } from '@/types';

import { replaceLocalMessage } from '../actions/messages';
import {
  closeMiddleSearch,
  exitForwardMode,
  fetchActiveConversationMembers,
  focusMessage,
  forwardMessage,
  hideLeftColumn,
  loadAllUnreadMessages,
  loadConversationMessages,
  loadConversationMessagesByDate,
  loadNewUnreadMessages,
  openChatInfo,
  openForwardMenu,
  openMiddleSearch,
  performMiddleSearch,
  resetMiddleSearch,
  selectSearchResult,
  sendAttachments,
  sendMessage,
  setForwardChat,
  showLeftColumn,
  toggleChatInfo,
  updateMiddleSearch,
} from '../actions/shared';
import type { Action, AllSlices, SliceCreator } from '../index';

export interface ForwardedMessage {
  fromChatId: number;
  messageId: number;
  toChatId?: number;
}

export interface MiddleSearchParams {
  requestedQuery?: string;
  fetchingQuery?: string;
  type: 'chat';
  results?: MiddleSearchResults;
}
export interface MiddleSearchResults {
  query: string;
  totalCount?: number;
  nextOffsetId?: number;
  foundMessages?: ApiMessage[];
}

export enum FocusDirection {
  Up,
  Down,
  Static,
}

export type ScrollTargetPosition = ScrollLogicalPosition | 'centerOrTop';

// Global Slice

interface SharedState {
  activeConversationId?: number;
  activeConversationMembers?: TelegramUser[];
  forwardedMessage?: ForwardedMessage;
  isForwardModalOpen: boolean;
  isLeftColumnShown: boolean;
  chatInfo: {
    isOpen: boolean;
    profileTab?: ProfileTabType;
  };
  middleSearch: {
    byConversationKey: Record<string, MiddleSearchParams | undefined>;
  };
  focusedMessage?: {
    conversationId?: number;
    messageId?: number;
    focusVersion?: number;
    direction?: FocusDirection;
    scrollTargetPosition?: ScrollTargetPosition;
  };
}

const initialState: SharedState = {
  activeConversationId: undefined,
  isForwardModalOpen: false,
  isLeftColumnShown: false,
  chatInfo: {
    isOpen: false,
  },
  middleSearch: {
    byConversationKey: {},
  },
};

interface SharedActions {
  setActiveConversationId: (id: number) => boolean;
  loadConversationMessages: ReturnType<typeof loadConversationMessages>;
  loadConversationMessagesByDate: ReturnType<typeof loadConversationMessagesByDate>;
  loadUnreadMessages: ReturnType<typeof loadAllUnreadMessages>;
  loadNewUnreadMessages: ReturnType<typeof loadNewUnreadMessages>;
  sendMessage: ReturnType<typeof sendMessage>;
  sendAttachments: ReturnType<typeof sendAttachments>;
  replaceLocalMessage: ReturnType<typeof replaceLocalMessage>;
  openForwardMenu: ReturnType<typeof openForwardMenu>;
  exitForwardMode: ReturnType<typeof exitForwardMode>;
  setForwardChat: ReturnType<typeof setForwardChat>;
  forwardMessage: ReturnType<typeof forwardMessage>;
  fetchActiveConversationMembers: ReturnType<typeof fetchActiveConversationMembers>;
  showLeftColumn: ReturnType<typeof showLeftColumn>;
  hideLeftColumn: ReturnType<typeof hideLeftColumn>;
  openChatInfo: ReturnType<typeof openChatInfo>;
  toggleChatInfo: ReturnType<typeof toggleChatInfo>;
  openMiddleSearch: ReturnType<typeof openMiddleSearch>;
  closeMiddleSearch: ReturnType<typeof closeMiddleSearch>;
  updateMiddleSearch: ReturnType<typeof updateMiddleSearch>;
  resetMiddleSearch: ReturnType<typeof resetMiddleSearch>;
  performMiddleSearch: ReturnType<typeof performMiddleSearch>;
  selectSearchResult: ReturnType<typeof selectSearchResult>;
  focusMessage: ReturnType<typeof focusMessage>;
}

export type SharedSlice = SharedState & SharedActions;

export const createSharedSlice: SliceCreator<AllSlices & SharedSlice, SharedSlice> = (set, get, store) => ({
  ...initialState,
  setActiveConversationId: (id) => {
    if (!get().conversations.byId[id]) {
      return false;
    }
    set(
      (state) => ({
        ...state,
        activeConversationId: id,
      }),
      undefined,
      'setActiveConversationId',
    );

    return true;
  },
  loadConversationMessages: loadConversationMessages(set, get, store),
  loadConversationMessagesByDate: loadConversationMessagesByDate(set, get, store),
  loadUnreadMessages: loadAllUnreadMessages(set, get, store),
  loadNewUnreadMessages: loadNewUnreadMessages(set, get, store),
  sendMessage: sendMessage(set, get, store),
  sendAttachments: sendAttachments(set, get, store),
  replaceLocalMessage: replaceLocalMessage(set, get, store),
  openForwardMenu: openForwardMenu(set, get, store),
  exitForwardMode: exitForwardMode(set, get, store),
  setForwardChat: setForwardChat(set, get, store),
  forwardMessage: forwardMessage(set, get, store),
  fetchActiveConversationMembers: fetchActiveConversationMembers(set, get, store),
  showLeftColumn: showLeftColumn(set, get, store),
  hideLeftColumn: hideLeftColumn(set, get, store),
  openChatInfo: openChatInfo(set, get, store),
  toggleChatInfo: toggleChatInfo(set, get, store),
  openMiddleSearch: openMiddleSearch(set, get, store),
  closeMiddleSearch: closeMiddleSearch(set, get, store),
  updateMiddleSearch: updateMiddleSearch(set, get, store),
  resetMiddleSearch: resetMiddleSearch(set, get, store),
  performMiddleSearch: performMiddleSearch(set, get, store),
  selectSearchResult: selectSearchResult(set, get, store),
  focusMessage: focusMessage(set, get, store),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SharedStoreAction<R extends (...args: any[]) => any> = Action<typeof createSharedSlice, R>;
